/**
 * Where this mould has been.
 *
 * Scans were being recorded and never read back, which left the product unable
 * to answer the one question it exists for. This is that answer: the ledger, in
 * order, newest first.
 *
 * Online-only, unlike the rest of the app. The ledger grows without limit and a
 * device has no use for all of it, so it is not replicated - and the screen says
 * so rather than showing an empty list that looks like "never scanned".
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiClientError, type ScanHistoryRow } from '@mouldtrack/shared';
import { History, MapPin, WifiOff } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Skeleton } from '../../components/ui/primitives.js';
import { Button } from '../../components/ui/button.js';
import { isNetworkError, listMouldScans } from '../../api/client.js';

export function MouldHistory({ mouldId }: { mouldId: string }) {
  const [rows, setRows] = useState<ScanHistoryRow[] | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  // How many there are in total, against how many have been fetched. A tool
  // scanned twice a day for two years has well over a thousand entries, and
  // showing the newest fifty with nothing to say so presents a truncated
  // history as a complete one - which is worse than showing none.
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(
    async (page = 1) => {
      setError(null);
      setOffline(false);
      if (page > 1) setLoadingMore(true);
      try {
        const res = await listMouldScans(mouldId, page);
        setTotal(res.pagination?.total ?? res.data.length);
        setRows((prev) => (page === 1 || !prev ? res.data : [...prev, ...res.data]));
      } catch (err) {
        if (page === 1) setRows([]);
        if (isNetworkError(err)) setOffline(true);
        else setError(err instanceof ApiClientError ? err.message : 'Could not load the history.');
      } finally {
        setLoadingMore(false);
      }
    },
    [mouldId],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const more = rows ? total - rows.length : 0;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="size-4 text-primary" aria-hidden />
          Movement history
        </CardTitle>
        {rows ? (
          <span className="text-xs text-muted-foreground">
            {rows.length === 0
              ? 'No scans yet'
              : more > 0
                ? `${rows.length} of ${total} scans`
                : `${total} scan${total === 1 ? '' : 's'}`}
          </span>
        ) : null}
      </CardHeader>

      <CardBody>
        {rows === undefined ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : offline ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <WifiOff className="size-4 shrink-0" aria-hidden />
            History needs a connection. It is not stored on this device.
          </p>
        ) : error ? (
          <p className="flex flex-wrap items-center gap-3 text-sm text-danger">
            {error}
            <Button size="sm" variant="secondary" onClick={() => void load(1)}>
              Try again
            </Button>
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This mould has not been scanned yet. Its location will appear here after the first scan.
          </p>
        ) : (
          <ol className="flex flex-col">
            {rows.map((row, i) => (
              <li key={row.id} className="flex gap-3">
                {/* A rail, so the eye reads it as a sequence rather than a table. */}
                <div className="flex flex-col items-center">
                  <span
                    className={
                      i === 0
                        ? 'mt-1.5 size-2.5 shrink-0 rounded-full bg-primary ring-4 ring-primary-subtle'
                        : 'mt-1.5 size-2.5 shrink-0 rounded-full bg-border-strong'
                    }
                  />
                  {i < rows.length - 1 ? <span className="w-px flex-1 bg-border" /> : null}
                </div>

                <div className={i < rows.length - 1 ? 'min-w-0 flex-1 pb-4' : 'min-w-0 flex-1'}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <p className="flex items-center gap-1.5 text-sm font-medium">
                      <MapPin className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      {row.checkpoint?.name ?? row.checkpoint?.code ?? 'Unknown checkpoint'}
                      {i === 0 ? (
                        <span className="text-xs font-normal text-primary">latest</span>
                      ) : null}
                    </p>
                    <time className="text-xs tabular-nums text-muted-foreground">
                      {new Date(row.scannedAt).toLocaleString()}
                    </time>
                  </div>
                  {/* Where, before which door. "Dispatch Gate" is ambiguous
                      across sites and every site has one; the answer someone
                      wants is "Mira Road". */}
                  {row.checkpoint?.location ? (
                    <p className="mt-0.5 text-sm">
                      {row.checkpoint.location.name}
                      {row.checkpoint.store ? (
                        <span className="text-muted-foreground">
                          {' · '}
                          {row.checkpoint.store.name}
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {row.checkpoint?.code}
                    {row.scannedBy ? ` · scanned by ${row.scannedBy.name}` : ''}
                  </p>
                  {row.remarks ? <p className="mt-1 text-sm">{row.remarks}</p> : null}
                </div>
              </li>
            ))}
          </ol>
        )}

        {more > 0 ? (
          <Button
            className="mt-3 w-full"
            variant="secondary"
            size="sm"
            loading={loadingMore}
            onClick={() => void load(Math.floor(rows!.length / 50) + 1)}
          >
            Show {Math.min(more, 50)} older {more === 1 ? 'scan' : 'scans'} ({more} left)
          </Button>
        ) : null}
      </CardBody>
    </Card>
  );
}
